/**
 * FilterBar Component
 *
 * Reusable filter bar for warehouse movements with date range and reference filters
 */

import React from 'react';
import { Card } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Separator } from '../ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { X, CalendarRange } from 'lucide-react';

function FilterBar({
  dateRange,
  onDateRangeChange,
  filters,
  onFilterChange,
  onClearFilters,
  showSalesOrderFilter = false,
  showPOFilter = false,
  showClientFilter = true,
  showSourceTypeFilter = false,
  sourceTypeFilterMode = 'inbound'
}) {
  const handleFilterFieldChange = (field, value) => {
    onFilterChange({
      ...filters,
      [field]: value
    });
  };

  return (
    <Card className="p-4 mb-4">
      <div className="flex gap-4 items-center flex-wrap">
        {/* Date Range Group */}
        <div className="flex items-center gap-2">
          <CalendarRange className="h-4 w-4 text-muted-foreground" />
          <div className="space-y-1">
            <Label htmlFor="startDate" className="text-xs">开始日期</Label>
            <Input
              id="startDate"
              type="date"
              className="h-8 min-w-[150px]"
              value={dateRange.startDate}
              onChange={(e) => onDateRangeChange('startDate', e.target.value)}
            />
          </div>
          <span className="text-sm text-muted-foreground">至</span>
          <div className="space-y-1">
            <Label htmlFor="endDate" className="text-xs">结束日期</Label>
            <Input
              id="endDate"
              type="date"
              className="h-8 min-w-[150px]"
              value={dateRange.endDate}
              onChange={(e) => onDateRangeChange('endDate', e.target.value)}
            />
          </div>
        </div>

        {/* Sales Order Name Filter (for Outbound) */}
        {showSalesOrderFilter && (
          <>
            <Separator orientation="vertical" className="h-12 mx-2" />
            <div className="space-y-1">
              <Label htmlFor="salesOrderName" className="text-xs">销售订单</Label>
              <Input
                id="salesOrderName"
                placeholder="输入销售订单名称"
                className="h-8 min-w-[200px]"
                value={filters.salesOrderName || ''}
                onChange={(e) => handleFilterFieldChange('salesOrderName', e.target.value)}
              />
            </div>
          </>
        )}

        {/* PO Name Filter (for Inbound) */}
        {showPOFilter && (
          <>
            <Separator orientation="vertical" className="h-12 mx-2" />
            <div className="space-y-1">
              <Label htmlFor="poName" className="text-xs">采购订单</Label>
              <Input
                id="poName"
                placeholder="输入采购订单名称"
                className="h-8 min-w-[200px]"
                value={filters.poName || ''}
                onChange={(e) => handleFilterFieldChange('poName', e.target.value)}
              />
            </div>
          </>
        )}

        {/* Client Name Filter */}
        {showClientFilter && (
          <>
            <Separator orientation="vertical" className="h-12 mx-2" />
            <div className="space-y-1">
              <Label htmlFor="clientName" className="text-xs">客户名称</Label>
              <Input
                id="clientName"
                placeholder="输入客户名称"
                className="h-8 min-w-[200px]"
                value={filters.clientName || ''}
                onChange={(e) => handleFilterFieldChange('clientName', e.target.value)}
              />
            </div>
          </>
        )}

        {/* Source Type Filter */}
        {showSourceTypeFilter && (
          <>
            <Separator orientation="vertical" className="h-12 mx-2" />
            <div className="space-y-1">
              <Label htmlFor="sourceType" className="text-xs">来源类型</Label>
              <Select
                value={filters.sourceType || 'all'}
                onValueChange={(value) => handleFilterFieldChange('sourceType', value)}
              >
                <SelectTrigger className="h-8 min-w-[140px]">
                  <SelectValue placeholder="全部类型" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">全部类型</SelectItem>
                  {sourceTypeFilterMode === 'inbound' ? (
                    <>
                      <SelectItem value="purchase_order">采购入库</SelectItem>
                      <SelectItem value="production_order">成品入库</SelectItem>
                      <SelectItem value="remaining_bar">余料入库</SelectItem>
                      <SelectItem value="sales_order">销售退货</SelectItem>
                      <SelectItem value="manual">手动入库</SelectItem>
                      <SelectItem value="cross_client_transfer">调拨入库</SelectItem>
                    </>
                  ) : (
                    <>
                      <SelectItem value="sales_order">销售出库</SelectItem>
                      <SelectItem value="material_requisition">领料出库</SelectItem>
                      <SelectItem value="manual">手动出库</SelectItem>
                      <SelectItem value="cross_client_transfer">调拨出库</SelectItem>
                    </>
                  )}
                </SelectContent>
              </Select>
            </div>
          </>
        )}

        {/* Clear Button */}
        <Button
          variant="outline"
          onClick={onClearFilters}
          className="ml-auto"
        >
          <X className="h-4 w-4 mr-2" />
          清除筛选
        </Button>
      </div>
    </Card>
  );
}

export default FilterBar;
